import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = `${BACKEND_URL}/api`;

const ACCESS_KEY = "ap_access";
const REFRESH_KEY = "ap_refresh";

export const authStore = {
  getAccess: () => localStorage.getItem(ACCESS_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  set: ({ access_token, refresh_token }) => {
    if (access_token) localStorage.setItem(ACCESS_KEY, access_token);
    if (refresh_token) localStorage.setItem(REFRESH_KEY, refresh_token);
  },
  clear: () => {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
  isAuthed: () => Boolean(localStorage.getItem(ACCESS_KEY)),
};

const client = axios.create({ baseURL: BASE });

client.interceptors.request.use((cfg) => {
  const tok = authStore.getAccess();
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  return cfg;
});

let refreshing = null;
async function refreshOnce() {
  if (refreshing) return refreshing;
  const rt = authStore.getRefresh();
  if (!rt) throw new Error("no refresh token");
  refreshing = axios
    .post(`${BASE}/auth/refresh`, { refresh_token: rt })
    .then((r) => {
      authStore.set(r.data);
      return r.data;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

client.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config || {};
    if (err.response?.status === 401 && !original._retry && authStore.getRefresh()) {
      original._retry = true;
      try {
        await refreshOnce();
        return client(original);
      } catch (e) {
        authStore.clear();
        if (typeof window !== "undefined") window.location.href = "/admin/login";
        throw e;
      }
    }
    throw err;
  },
);

export const api = client;

export const authApi = {
  login: (email, password) =>
    client.post("/auth/login", { email, password }).then((r) => r.data),
  logout: () => {
    const rt = authStore.getRefresh();
    return client.post("/auth/logout", { refresh_token: rt }).catch(() => null);
  },
  me: () => client.get("/auth/me").then((r) => r.data),
  requestReset: (email) =>
    axios.post(`${BASE}/auth/password-reset/request`, { email }).then((r) => r.data),
  confirmReset: (token, new_password) =>
    axios
      .post(`${BASE}/auth/password-reset/confirm`, { token, new_password })
      .then((r) => r.data),
};

export const adminApi = {
  orgSummary: () => client.get("/admin/orgs/summary").then((r) => r.data),
  users: () => client.get("/admin/users").then((r) => r.data),
  legalEntities: () => client.get("/admin/legal-entities").then((r) => r.data),
  updateLegalEntity: (id, patch) =>
    client.put(`/admin/legal-entities/${id}`, patch).then((r) => r.data),
  getSettings: (key) => client.get(`/admin/settings/${key}`).then((r) => r.data),
  putSettings: (key, value) =>
    client.put(`/admin/settings/${key}`, { value }).then((r) => r.data),
};

export const catalogApi = {
  categories: () => axios.get(`${BASE}/catalog/categories`).then((r) => r.data),
  products: (params = {}) =>
    axios.get(`${BASE}/catalog/products`, { params }).then((r) => r.data),
  product: (slug) => axios.get(`${BASE}/catalog/products/${slug}`).then((r) => r.data),
};
